import { createApi } from '@reduxjs/toolkit/dist/query/react';

import * as endpoints from '../endpoints';
import { HTTP_METHOD, REDUX_CACHE, SUPPORT_REQUESTS } from '../constants';
import {
  getBaseQuery,
  getListProvidesTags,
  getUrlParamsString,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import {
  ISupportRequest,
  ISupportRequestListResponse,
  ListSupportRequestsQueryDto,
  UpdateSupportRequestStatusDto,
} from '../../../types/interfaces/support-request';

export const supportAdminApi = createApi({
  reducerPath: 'supportAdminApi',
  tagTypes: [
    SUPPORT_REQUESTS.SUPPORT_REQUEST,
    SUPPORT_REQUESTS.ACTIVE_SUPPORT_REQUESTS,
    SUPPORT_REQUESTS.ARCHIVED_SUPPORT_REQUESTS,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(endpoints.AdminSupport.ROOT),
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getSupportRequestsApi: builder.query<
      ISupportRequest[],
      ListSupportRequestsQueryDto
    >({
      keepUnusedDataFor: 3600,
      query(params = {}) {
        const url =
          endpoints.AdminSupport.REQUESTS +
          '?' +
          getUrlParamsString({
            page: params.page ?? 1,
            limit: params.limit ?? 20,
            search: params.search,
            status: params.status,
          });

        return {
          url,
          method: HTTP_METHOD.GET,
        };
      },
      transformResponse: (response: ISupportRequestListResponse) =>
        response?.items ?? [],
      providesTags: (result) =>
        getListProvidesTags(result, SUPPORT_REQUESTS.ACTIVE_SUPPORT_REQUESTS),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${JSON.stringify(queryArgs)}`;
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getArchivedSupportRequestsApi: builder.query<
      ISupportRequest[],
      Omit<ListSupportRequestsQueryDto, 'status'>
    >({
      keepUnusedDataFor: 3600,
      query(params = {}) {
        const url =
          endpoints.AdminSupport.ARCHIVED_REQUESTS +
          '?' +
          getUrlParamsString({
            page: params.page ?? 1,
            limit: params.limit ?? 20,
            search: params.search,
          });

        return {
          url,
          method: HTTP_METHOD.GET,
        };
      },
      transformResponse: (response: ISupportRequestListResponse) =>
        response?.items ?? [],
      providesTags: (result) =>
        getListProvidesTags(result, SUPPORT_REQUESTS.ARCHIVED_SUPPORT_REQUESTS),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${JSON.stringify(queryArgs)}`;
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    updateSupportRequestStatusApi: builder.mutation<
      { id: string; status: number },
      { requestId: string; body: UpdateSupportRequestStatusDto }
    >({
      query({ requestId, body }) {
        return {
          url: endpoints.AdminSupport.STATUS(requestId),
          method: HTTP_METHOD.PUT,
          body,
        };
      },
      invalidatesTags: (result, error, { requestId }) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }

        return [
          { type: SUPPORT_REQUESTS.SUPPORT_REQUEST, id: requestId },
          {
            type: SUPPORT_REQUESTS.ACTIVE_SUPPORT_REQUESTS,
            id: REDUX_CACHE.LIST,
          },
          {
            type: SUPPORT_REQUESTS.ARCHIVED_SUPPORT_REQUESTS,
            id: REDUX_CACHE.LIST,
          },
        ];
      },
      ...TransformedResponseWithNotification(),
    }),
  }),
});

export const {
  useLazyGetSupportRequestsApiQuery: useLazyGetSupportRequests,
  useLazyGetArchivedSupportRequestsApiQuery: useLazyGetArchivedSupportRequests,
  useUpdateSupportRequestStatusApiMutation: useUpdateSupportRequestStatus,
} = supportAdminApi;
